import { useTranslation } from '@multaqa/i18n'
import { Panel, PanelContent, PanelHeader, PanelTitle } from '@multaqa/ui/components/ui/panel'
import { useEffect, useMemo, useState } from 'react'
import type { CompanySettingResource } from './api'
import { useCompanyLogoPreview, useUpdateCompanySettings } from './api'
import { SettingsCard } from './company-settings-card'
import { SettingsNumberField } from './company-settings-fields'

export function CompanyHrCard({
  groupId,
  companyId,
  settings,
}: {
  groupId: string
  companyId: string
  settings: CompanySettingResource
}) {
  const { t } = useTranslation()
  const update = useUpdateCompanySettings()
  const [editing, setEditing] = useState(false)
  const [minutes, setMinutes] = useState(String(settings.permission_monthly_minutes))
  const [message, setMessage] = useState<string | null>(null)
  const save = () => {
    if (!/^\d+$/.test(minutes.trim()) || Number(minutes) < 1) {
      setMessage(t('companies.settings.form.wholeNumber'))
      return
    }
    update.mutate(
      { groupId, companyId, input: { permission_monthly_minutes: Number(minutes) } },
      {
        onSuccess: () => {
          setEditing(false)
          setMessage(null)
        },
        onError: () => setMessage(t('companies.settings.form.saveFailed')),
      },
    )
  }
  return (
    <SettingsCard
      testId="company-settings-hr"
      title={t('companies.settings.groups.hr')}
      description={t('companies.settings.groups.hrDescription')}
      editing={editing}
      pending={update.isPending}
      error={Boolean(update.error)}
      message={message}
      onEdit={() => {
        setMinutes(String(settings.permission_monthly_minutes))
        setMessage(null)
        setEditing(true)
      }}
      onCancel={() => {
        setMinutes(String(settings.permission_monthly_minutes))
        setMessage(null)
        setEditing(false)
      }}
      onSave={save}
    >
      {editing ? (
        <SettingsNumberField
          id="company-settings-permission-minutes"
          label={t('companies.settings.fields.permissionMonthlyMinutes')}
          value={minutes}
          min="1"
          onChange={setMinutes}
        />
      ) : (
        <Value
          label={t('companies.settings.fields.permissionMonthlyMinutes')}
          value={String(settings.permission_monthly_minutes)}
        />
      )}
    </SettingsCard>
  )
}

export function CompanyBrandingCard({
  groupId,
  companyId,
  settings,
}: {
  groupId: string
  companyId: string
  settings: CompanySettingResource
}) {
  const { t } = useTranslation()
  return (
    <SettingsCard
      testId="company-settings-branding"
      title={t('companies.settings.groups.branding')}
      description={t('companies.settings.groups.brandingDescription')}
      editing={false}
      pending={false}
      error={false}
      readOnly
      onEdit={() => {}}
      onCancel={() => {}}
      onSave={() => {}}
    >
      <div className="grid gap-x-6 sm:grid-cols-2">
        <Value
          label={t('companies.settings.fields.accentColor')}
          value={settings.branding_accent_color ?? t('companies.common.empty')}
        />
        <LogoValue
          groupId={groupId}
          companyId={companyId}
          logoPath={settings.branding_logo_path ?? null}
        />
      </div>
    </SettingsCard>
  )
}

export function CompanyWarehousingCard({ settings }: { settings: CompanySettingResource }) {
  const { t } = useTranslation()
  if (!settings.default_returns_warehouse_id && !settings.default_fulfilment_warehouse_id)
    return null
  return (
    <Panel data-testid="company-settings-warehousing">
      <PanelHeader>
        <div>
          <PanelTitle>{t('companies.settings.groups.warehousing')}</PanelTitle>
          <p className="text-small text-muted-foreground mt-0.5">
            {t('companies.settings.groups.warehousingDescription')}
          </p>
        </div>
      </PanelHeader>
      <PanelContent className="p-4">
        <div className="grid gap-x-6 sm:grid-cols-2">
          {settings.default_returns_warehouse_id ? (
            <Value
              label={t('companies.settings.fields.returnsWarehouse')}
              value={settings.default_returns_warehouse_id}
            />
          ) : null}
          {settings.default_fulfilment_warehouse_id ? (
            <Value
              label={t('companies.settings.fields.fulfilmentWarehouse')}
              value={settings.default_fulfilment_warehouse_id}
            />
          ) : null}
        </div>
      </PanelContent>
    </Panel>
  )
}

/** The uploaded logo itself, as it prints on the company's PDFs (f425). */
function LogoValue({
  groupId,
  companyId,
  logoPath,
}: {
  groupId: string
  companyId: string
  logoPath: string | null
}) {
  const { t } = useTranslation()
  const preview = useCompanyLogoPreview(groupId, companyId, logoPath)
  const blob = logoPath ? preview.data : undefined
  const url = useMemo(() => (blob ? URL.createObjectURL(blob) : null), [blob])
  // The object URL is a browser resource outside React; free it when it changes.
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url)
    },
    [url],
  )
  const label = t('companies.settings.fields.logo')

  return (
    <div className="border-b border-border py-3">
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      {url ? (
        <img
          src={url}
          alt={label}
          className="mt-1 h-12 w-auto max-w-48 object-contain"
          data-testid="company-settings-logo"
        />
      ) : (
        <div className="text-body font-mono">{t('companies.common.empty')}</div>
      )}
    </div>
  )
}

function Value({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-border py-3">
      <div className="text-label text-muted-foreground font-mono uppercase">{label}</div>
      <div className="text-body font-mono break-all">{value}</div>
    </div>
  )
}
